import { configuredAxios } from '../../../modules/axios-error-handler';
import { memoize } from 'lodash';

import { IPerformancePricing, IPackagePricing } from '../custom/classes/production';

export default class PackageApi {
    private static acePackageAPIRoute = process.env.PACKAGE_API_ROUTE;

    private static performancePricingCache = memoize(PackageApi.getPerformancePricingApi, (performanceId: number, facilityId: number, packageId: number) => `perf-${performanceId}-facility-${facilityId}-packageId-${packageId}` as any);

    private static packagePricingCache = memoize(PackageApi.getPackagePricingApi, (packageId: number, facilityId: number) => `package-${packageId}-facility-${facilityId}`);

    private static filtersSource = configuredAxios.CancelToken.source();


    private static getPerformancePricingApi(performanceId: number, facilityId: number, packageId: number): Promise<IPerformancePricing[]> {

        var requestString = `/${this.acePackageAPIRoute}${process.env.PERFORMANCE_PRICING_ROUTE}?performanceId=${performanceId}&facilityId=${facilityId}&packageId=${packageId}`;

        return configuredAxios.get<IPerformancePricing[]>(requestString, {
            withCredentials: true,
            cancelToken: this.filtersSource.token
        }).then(response => response.data);
    }

    private static getPackagePricingApi(performanceId: number, facilityId: number): Promise<IPackagePricing> {

        var requestString = `/${this.acePackageAPIRoute}${process.env.PACKAGE_PRICING_ROUTE}?packageId=${performanceId}&facilityId=${facilityId}`;

        return configuredAxios.get<IPerformancePricing>(requestString, {
            withCredentials: true,
            cancelToken: this.filtersSource.token
        }).then(response => response.data);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getPerformancePricing(performanceId: number, facilityId: number, packageId: number): Promise<IPerformancePricing[]> {

        //return this.getPackagePricingApi(packageId, performanceIds);

        // This is the memoized variant. Since the request is cancelable, memoize would need to be tweaked to be properly utilized.
        return this.performancePricingCache(performanceId, facilityId, packageId);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getPackagePricing(packageId: number, facilityId: number): Promise<IPackagePricing> {

        // This is the memoized variant. Since the request is cancelable, memoize would need to be tweaked to be properly utilized.
        return this.packagePricingCache(packageId, facilityId);
    }

    /**
     * Call this to clear the memoized events and start fresh.
     */
    public static clearPackagePricingCache(): void {
        return this.packagePricingCache.cache.clear();
    }

    /**
     * Call this to clear the memoized events and start fresh.
     */
    public static clearPerformancePricingCache(): void {
        return this.performancePricingCache.cache.clear();
    }

    public static processFlexPackage(packageId: number,
        performanceZones: { [key: number]: number; },
        requestedPriceTypeIds: Array<number>,
        isUnseated: boolean,
        orderNotes: string,
        configId: number) {

        var requestString = `/${this.acePackageAPIRoute}/ProcessPackageRequest`

        const packageRequest = {
            packageId,
            performanceZones,
            requestedPriceTypeIds,
            seatedRequest: !isUnseated,
            orderNotes,
            configId
        };

        return configuredAxios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static processSuperPackage(packageId: number,
        subPackageZones: { [key: number]: number; },
        requestedPriceTypeIds: Array<number>,
        orderNotes: string,
        configId: number) {

        var requestString = `/${this.acePackageAPIRoute}/ProcessSuperPackageRequest`

        const packageRequest = {
            packageId,
            subPackageZones,
            requestedPriceTypeIds,
            orderNotes,
            configId
        };

        return configuredAxios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static processFixedPackage(packageId: number,
        zoneId: number,
        requestedPriceTypeIds: Array<number>,
        orderNotes: string,
        configId: number, seatingPreferences: string) {

        var requestString = `/${this.acePackageAPIRoute}/ProcessFixedPackageRequest`

        const packageRequest = {
            packageId,
            zoneId,
            requestedPriceTypeIds,
            orderNotes,
            configId,
            seatingPreferences
        };

        return configuredAxios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static flexPackageSessionVariable(packageId: number,
        performanceIds: string) {

        const requestString = `/${this.acePackageAPIRoute}/FlexPackageSessionVariable`

        const packageRequest = {
            packageId,
            performanceIds
        };

        return configuredAxios.post(requestString, packageRequest,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }

    public static sendPwycAmounts(packageId: number,
        performancePriceTypeAmounts: { [performanceId: number]: { [priceTypeId: number]: number } }) {

        const requestString = `/${this.acePackageAPIRoute}/FlexPackagePwycAmounts`;

        const request = {
            packageId,
            performancePriceTypeAmounts
        };

        return configuredAxios.post(requestString, request,
            {
                method: "POST"
            }
        ).then(response => response.data);
    }
}
